import { useEffect, useRef, useState } from "react";
import {
  AssetMessageBody,
  AssetMessageUpdate,
  GamgeeChatMessageRead,
  AssetMessageFinalOutput,
  AssetMessageError,
  AssetMessageClarificationQuestion
} from "~/types/message";
import { addUpdateToTracker } from "./utils";
import UpdateSection from "./update-section";
import FinalOutput from "./final-output";
import { getEventStream } from "~/lib/api";
import { useGlobalStore } from "~/stores/store-provider";
import MarkdownContainer from "~/components/markdown-container/markdown-container";

const AssetMessage = ({ message }: { message: GamgeeChatMessageRead }) => {
  const assetMessage = message.message as AssetMessageBody;
  const [isStreaming, setIsStreaming] = useState(assetMessage.streaming);
  const [updates, setUpdates] = useState<AssetMessageUpdate[]>(assetMessage.updates);
  const [updateIdsInOrder, setUpdateIdsInOrder] = useState<string[]>([]);
  const [finalOutput, setFinalOutput] = useState<AssetMessageFinalOutput | AssetMessageError | AssetMessageClarificationQuestion | null>(assetMessage.final_output);
  const updateTracker = useRef<any>({});
  
  const { markAssetStreamingFinished } = useGlobalStore((state: any) => state);
  
  const handleUpdate = (update: AssetMessageUpdate) => {
    if (update.type === "update") {
      const { id } = update.data as { id: string };
      if (!updateTracker.current[id]) {
        setUpdateIdsInOrder((prev) => [...prev, id]);
      }
      addUpdateToTracker(updateTracker.current, update);
    }
  }

  const parseEventMessage = (eventMessage: string) => {
    const [channelName, eventMessages] = JSON.parse(eventMessage);
    const messages = eventMessages.map((eventMessage: any) => {
      return JSON.parse(eventMessage[1].data);
    });
    return { channelName, messages };
  };


  useEffect(() => {
    updates.forEach((update) => handleUpdate(update));
  }, []);

  // Sync local state when message prop changes (after refetch)
  useEffect(() => {
    const newAssetMessage = message.message as AssetMessageBody;
    setIsStreaming(newAssetMessage.streaming);
    setFinalOutput(newAssetMessage.final_output);
  }, [message]);

  useEffect(() => {
    if (isStreaming) {
      const eventSource = getEventStream(
        `/api/v1/chat/connectToStream?session_id=${message.session_id}&message_id=${message._id}`
      );
      
      eventSource.addEventListener("message", (event) => {
        const { messages } = parseEventMessage(event.data);
        
        for (const msg of messages) {
          if (msg.type === "system" && msg.data === "session_end") {
            markAssetStreamingFinished(message._id);
            setIsStreaming(false);
            eventSource.close();
            return;
          }
          
          // Handle AssetMessageUpdate objects
          if (msg.type === "update") {
            setUpdates((prev) => [...prev, msg]);
            handleUpdate(msg);
          }
        }
      });
      
      eventSource.addEventListener("error", (event) => {
        console.log("Event source error:", event);
        markAssetStreamingFinished(message._id);
        setIsStreaming(false);
        eventSource.close();
      });

      return () => {
        eventSource.close();
      };
    }
  }, [isStreaming, message._id, message.session_id]);

  return (
    <div className="flex flex-col gap-4 p-5 rounded-xl my-2 mx-0 max-w-full">
      <UpdateSection
        updateIdsInOrder={updateIdsInOrder}
        updateTracker={updateTracker}
        message={message}
      />
      {finalOutput && (
        <>
          {(finalOutput as AssetMessageFinalOutput).output && (
            <FinalOutput 
              finalOutput={finalOutput as AssetMessageFinalOutput}
              message={message}
            />
          )}
          {(finalOutput as AssetMessageError).error && (
            <div className="border-l-4 border-error bg-error/10 text-error-content p-4">
              <MarkdownContainer message={message}>
                {(finalOutput as AssetMessageError).error}
              </MarkdownContainer>
            </div>
          )}
          {(finalOutput as AssetMessageClarificationQuestion).question && (
            <div className="bg-base-200 p-8 rounded-xl">
              <MarkdownContainer message={message}>
                {(finalOutput as AssetMessageClarificationQuestion).question}
              </MarkdownContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AssetMessage;
